// owned by WP-SEC-UI · the arrival message after a sign-in (shell-ia §11.1): "Welcome back. Last sign-in Sun 11 Oct, 21:14 GST · Chrome on
// macOS" — no name (MoET review, 16 Oct 2026: the screen names no one, so a demo shows no person), plus "2 other sessions are active · Review" when there are; 8 s, centre-bottom above the status bar
// (DESIGN-SYSTEM §10.17). An administrator notice from the sign-in ("Your password was reset by … on …") shows once as its own line.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Info, X } from "lucide-react";
import { useAuth } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { formatDateTimeGst, formatLongDate } from "../lib/time";
import { deviceLabel, useSessionInfo } from "./useSessionInfo";
import { softNavigate } from "./navigate";

const SHOW_MS = 8_000;

export function LastSignInToast() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { session, acknowledge } = useAuth();
  const { info } = useSessionInfo();
  const [shown, setShown] = useState<{ until: number } | null>(null);
  const arrival = !!session?.arrival;

  useEffect(() => {
    if (!arrival) return;
    setShown({ until: Date.now() + SHOW_MS });
    acknowledge("arrival");
  }, [arrival, acknowledge]);

  useEffect(() => {
    if (!shown) return;
    const timer = window.setTimeout(() => setShown(null), Math.max(0, shown.until - Date.now()));
    return () => window.clearTimeout(timer);
  }, [shown]);

  const notices = session?.notices ?? [];
  if ((!shown && notices.length === 0) || !session) return null;

  const previous = info?.previous ?? (session.previousSignIn ? { at: session.previousSignIn.at, device: session.previousSignIn.device ?? "" } : null);
  const device = deviceLabel(previous?.device);
  const when = previous ? formatDateTimeGst(previous.at, locale) : "";
  const others = info?.otherActiveSessions ?? 0;

  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 z-[70] flex flex-col items-center gap-2 px-4" style={{ bottom: "calc(var(--econic-chrome-bottom, var(--econic-status-h, 0px)) + 16px)" }}>
      {notices.map((n) => (
        <div key={`${n.code}-${n.at ?? ""}`} role="status" className="ec-toast pointer-events-auto max-w-[min(640px,100%)]" style={{ background: "var(--st-warn-bg)", color: "var(--label)" }}>
          <Info aria-hidden style={{ color: "var(--st-warn)" }} />
          <span className="min-w-0 flex-1">{t(`auth.notice.${n.code}`, { name: n.by ?? "", date: n.at ? formatLongDate(n.at, locale) : "", defaultValue: t("assistant:econic.session.notice.title") })}</span>
          <button type="button" className="ec-btn ghost sm icon" aria-label={t("auth.notice.dismiss")} title={t("auth.notice.dismiss")} onClick={() => acknowledge("notices")}>
            <X aria-hidden />
          </button>
        </div>
      ))}
      {shown ? (
        <div role="status" className="ec-toast pointer-events-auto max-w-[min(720px,100%)]" data-testid="last-signin-toast">
          <CheckCircle2 aria-hidden />
          <span className="min-w-0 flex-1">
            <b className="font-semibold">{t(previous ? "assistant:econic.session.welcome" : "assistant:econic.session.welcomeFirst")}</b>{" "}
            {previous
              ? device
                ? t("assistant:econic.session.lastSignIn", { when, device })
                : t("assistant:econic.session.lastSignInAt", { when })
              : t("assistant:econic.session.firstSignIn")}
            {others > 0 ? <> · {t("assistant:econic.session.others", { count: others })}</> : null}
          </span>
          {others > 0 ? (
            <button type="button" className="ec-btn ghost sm" onClick={() => { setShown(null); softNavigate("/settings/security"); }}>
              {t("assistant:econic.session.review")}
            </button>
          ) : null}
          <button type="button" className="ec-btn ghost sm icon" aria-label={t("assistant:econic.session.dismiss")} title={t("assistant:econic.session.dismiss")} onClick={() => setShown(null)}>
            <X aria-hidden />
          </button>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
